/**
 * WelcomeState - Owner/admin onboarding flow for new organizations
 */
import { useNavigate } from "react-router-dom";
import { Button } from "../../components/ui/button";
import StatusBadge from "./StatusBadge";
import {
  Building2,
  CheckCircle2,
  FileText,
  MessageSquare,
  UserPlus,
  Upload,
  Sparkles,
  Lock,
} from "lucide-react";

const SUGGESTED_PROMPTS = [
  "What are the grievance filing deadlines?",
  "Summarize overtime and premium pay obligations",
  "What disciplinary procedures must management follow?",
  "List all seniority-related provisions",
];

export default function WelcomeState({ org, members, usage, agreementCount }) {
  const navigate = useNavigate();
  const hasUpload = agreementCount > 0;
  const acceptedCount = members.filter((m) => m.invite_status === "accepted").length;

  return (
    <div className="max-w-5xl mx-auto" data-testid="org-welcome-state">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Main content — steps */}
        <div className="lg:col-span-2 space-y-5">
          {/* Header */}
          <div className="mb-2">
            <h1 className="font-heading text-2xl font-semibold mb-1" data-testid="welcome-heading">
              Your Organization Workspace Is Ready
            </h1>
            <p className="text-muted-foreground text-sm">
              Get your team up and running in three simple steps.
            </p>
          </div>

          {/* Step 1 — Upload */}
          <div
            className={`relative bg-card border rounded-lg p-5 transition ${
              hasUpload ? "border-emerald-200" : "border-primary/30 shadow-sm"
            }`}
            data-testid="welcome-step-1"
          >
            <div className="flex items-start gap-4">
              <div
                className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${
                  hasUpload
                    ? "bg-emerald-100 text-emerald-600"
                    : "bg-primary/10 text-primary"
                }`}
              >
                {hasUpload ? (
                  <CheckCircle2 className="w-5 h-5" />
                ) : (
                  <span className="text-sm font-semibold">1</span>
                )}
              </div>
              <div className="flex-1">
                <h3 className="font-medium text-sm mb-1">
                  Upload Your Primary Agreement
                </h3>
                <p className="text-xs text-muted-foreground mb-3">
                  Start with the agreement your team references most often. This
                  becomes your shared foundation.
                </p>
                {!hasUpload ? (
                  <Button
                    size="sm"
                    onClick={() => navigate("/upload")}
                    data-testid="welcome-upload-btn"
                  >
                    <Upload className="w-3.5 h-3.5 mr-1.5" />
                    Upload Agreement
                  </Button>
                ) : (
                  <p className="text-xs text-emerald-600 font-medium flex items-center gap-1.5">
                    <CheckCircle2 className="w-3.5 h-3.5" />
                    {agreementCount} agreement{agreementCount !== 1 ? "s" : ""} uploaded
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Step 2 — First review */}
          <div
            className={`relative bg-card border rounded-lg p-5 transition ${
              hasUpload
                ? "border-primary/30 shadow-sm"
                : "border-border opacity-60"
            }`}
            data-testid="welcome-step-2"
          >
            {!hasUpload && (
              <div className="absolute inset-0 rounded-lg bg-background/40 z-10 flex items-center justify-center">
                <span className="flex items-center gap-1.5 text-xs text-muted-foreground bg-muted px-3 py-1.5 rounded-full">
                  <Lock className="w-3 h-3" />
                  Upload an agreement first
                </span>
              </div>
            )}
            <div className="flex items-start gap-4">
              <div
                className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${
                  hasUpload
                    ? "bg-primary/10 text-primary"
                    : "bg-muted text-muted-foreground"
                }`}
              >
                <span className="text-sm font-semibold">2</span>
              </div>
              <div className="flex-1">
                <h3 className="font-medium text-sm mb-1">
                  Run Your First Structured Review
                </h3>
                <p className="text-xs text-muted-foreground mb-3">
                  Ask common questions to extract obligations, timelines, and
                  procedures instantly.
                </p>
                <div className="space-y-1.5">
                  {SUGGESTED_PROMPTS.map((prompt, i) => (
                    <button
                      key={i}
                      disabled={!hasUpload}
                      onClick={() => hasUpload && navigate("/ask")}
                      className="w-full text-left flex items-center gap-2 px-3 py-2 bg-muted/50 rounded-md text-xs text-foreground/70 hover:bg-muted transition disabled:cursor-not-allowed"
                      data-testid={`suggested-prompt-${i}`}
                    >
                      <Sparkles className="w-3 h-3 text-primary/60 flex-shrink-0" />
                      {prompt}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Step 3 — Invite team */}
          <div
            className="relative bg-card border border-border rounded-lg p-5"
            data-testid="welcome-step-3"
          >
            <div className="flex items-start gap-4">
              <div className="w-9 h-9 rounded-full bg-muted flex items-center justify-center flex-shrink-0 text-muted-foreground">
                <span className="text-sm font-semibold">3</span>
              </div>
              <div className="flex-1">
                <h3 className="font-medium text-sm mb-1">
                  Invite Your Core Team
                </h3>
                <p className="text-xs text-muted-foreground mb-3">
                  Bring in 2–3 team members to collaborate and share insights.
                </p>
                <div className="flex items-center gap-3">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => navigate("/org?tab=members")}
                    data-testid="welcome-invite-btn"
                  >
                    <UserPlus className="w-3.5 h-3.5 mr-1.5" />
                    Invite Members
                  </Button>
                  <span className="text-xs text-muted-foreground">
                    {acceptedCount} / 10 members
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-4" data-testid="welcome-sidebar">
          {/* Org info card */}
          <div className="bg-card border border-border rounded-lg p-4 space-y-3">
            <div className="flex items-center gap-2.5">
              <Building2 className="w-4 h-4 text-primary" />
              <span className="font-medium text-sm">{org.name}</span>
            </div>
            <div className="space-y-2 text-xs">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Status</span>
                <StatusBadge status={org.status} />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Members</span>
                <span className="font-medium">{acceptedCount} / 10</span>
              </div>
            </div>
          </div>

          {/* Usage meters */}
          {usage && (
            <div className="bg-card border border-border rounded-lg p-4 space-y-3">
              <p className="text-xs text-muted-foreground uppercase tracking-wider font-medium">
                Usage This Month
              </p>
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <MessageSquare className="w-3 h-3" />
                    Questions
                  </span>
                  <span className="font-medium text-xs" data-testid="sidebar-questions">
                    {usage.questions_used}
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <FileText className="w-3 h-3" />
                    Case Reviews
                  </span>
                  <span className="font-medium text-xs" data-testid="sidebar-reviews">
                    {usage.reviews_used}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
